/**
 * Generic country -> state/province select sync, reusable across any CMS
 * form (rooms, venues, casinos, etc.) that pairs a country select with a
 * dependent state select.
 *
 * Auto-discovers pairs via data attributes rendered by the PHP-side
 * `PN\Component\CMS\Form\CountryStateFieldSync` helper:
 *  - the state field's row carries `data-country-field` (the country field's
 *    name), `data-countries-with-states` (comma list) and
 *    `data-states-by-country` (JSON map of country -> states);
 *  - the state `<select>` is looked up inside that row, and the country
 *    `<select>` by name within the enclosing form, so no ids are required.
 *
 * Usage: call `initCountryStateSync()` once on DOM ready (see tablerjs.ts).
 */

type StatesByCountry = Record<string, Record<string, string>>;

export class CountryStateSync {
  private readonly countrySelect: HTMLSelectElement;

  private readonly stateRow: HTMLElement;

  private readonly stateSelect: HTMLSelectElement;

  private readonly countriesWithStates: string[];

  private readonly statesByCountry: StatesByCountry;

  private readonly placeholderOption: HTMLOptionElement | null;

  constructor(countrySelect: HTMLSelectElement, stateRow: HTMLElement, stateSelect: HTMLSelectElement) {
    this.countrySelect = countrySelect;
    this.stateRow = stateRow;
    this.stateSelect = stateSelect;
    this.countriesWithStates = (stateRow.dataset.countriesWithStates ?? '')
      .split(',')
      .map((countryId) => countryId.trim())
      .filter((countryId) => countryId !== '');
    this.statesByCountry = CountryStateSync.parseStatesByCountry(stateRow.dataset.statesByCountry);

    const placeholder = stateSelect.querySelector('option[value=""]');
    this.placeholderOption = placeholder instanceof HTMLOptionElement
      ? (placeholder.cloneNode(true) as HTMLOptionElement)
      : null;
  }

  init(): void {
    this.countrySelect.addEventListener('change', () => this.onCountryChange());
    this.toggleState();
  }

  private onCountryChange(): void {
    this.populateStates(this.countrySelect.value);
    this.toggleState();
  }

  private toggleState(): void {
    const isVisible = this.countriesWithStates.includes(this.countrySelect.value);
    // Mirrors the server-rendered `hidden` attribute set by
    // PN\Component\CMS\Form\CountryStateFieldSync. A CSS utility class is not
    // used because the CMS form theme rebuilds the row's class attribute.
    this.stateRow.hidden = !isVisible;
  }

  private populateStates(countryId: string): void {
    const states = this.statesByCountry[countryId] ?? {};
    const currentValue = this.stateSelect.value;

    this.stateSelect.innerHTML = '';
    if (this.placeholderOption) {
      this.stateSelect.appendChild(this.placeholderOption.cloneNode(true));
    }

    Object.entries(states).forEach(([stateId, label]) => {
      const option = document.createElement('option');
      option.value = stateId;
      option.textContent = label;
      this.stateSelect.appendChild(option);
    });

    const hasCurrentValue = Array.from(this.stateSelect.options)
      .some((option) => option.value === currentValue);
    this.stateSelect.value = hasCurrentValue ? currentValue : '';
  }

  private static parseStatesByCountry(raw: string | undefined): StatesByCountry {
    if (!raw) {
      return {};
    }

    try {
      const parsed: unknown = JSON.parse(raw);

      return typeof parsed === 'object' && parsed !== null
        ? (parsed as StatesByCountry)
        : {};
    } catch {
      return {};
    }
  }
}

export function initCountryStateSync(): void {
  document.querySelectorAll<HTMLElement>('[data-country-field]').forEach((stateRow) => {
    const countryField = stateRow.dataset.countryField;
    const stateSelect = stateRow.querySelector('select');

    if (!countryField || !(stateSelect instanceof HTMLSelectElement)) {
      return;
    }

    // Resolve the country select by field NAME, scoped to the enclosing form.
    // Matches both un-prefixed (`countryId`) and prefixed (`casino[countryId]`)
    // names. Ids are not usable here: Symfony emits a duplicate `id` attribute
    // when a FormType sets `attr.id`, and the browser keeps the generated one.
    const scope: ParentNode = stateRow.closest('form') ?? document;
    const countrySelect = scope.querySelector(
      `select[name="${countryField}"], select[name$="[${countryField}]"]`,
    );

    if (!(countrySelect instanceof HTMLSelectElement)) {
      return;
    }

    new CountryStateSync(countrySelect, stateRow, stateSelect).init();
  });
}

